import { graphql, readInlineData } from 'react-relay';

import {
    PricingGuidancePriceRange,
    pricingGuidanceHelpers_item$key,
    pricingGuidanceHelpers_item$data,
} from './__generated__/pricingGuidanceHelpers_item.graphql';
import { pricingGuidanceHelpers_viewer$key } from './__generated__/pricingGuidanceHelpers_viewer.graphql';

const priceRanges: PricingGuidancePriceRange[] = [
    'OUT_OF_RANGE_LOW',
    'LOWER_RANGE',
    'NEAR_MEDIAN_LOW',
    'MEDIAN',
    'NEAR_MEDIAN_HIGH',
    'HIGHER_RANGE',
    'OUT_OF_RANGE_HIGH',
];

const viewerFragment = graphql`
    fragment pricingGuidanceHelpers_viewer on Viewer @inline {
        pdpSiderailSoldPricingGuidanceLinkFF: featureFlag(
            feature: "pdpSiderailSoldPricingGuidanceLink"
        )
    }
`;

const itemFragment = graphql`
    fragment pricingGuidanceHelpers_item on Item @inline {
        pricingGuidance(isTrade: $isTrade) {
            pricingGuidanceType
            priceRange
            price {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
    }
`;

export const getPriceAmountByCurrency = ({
    price,
    currency,
}: {
    price?: NonNullable<pricingGuidanceHelpers_item$data['pricingGuidance']>['price'];
    currency: string;
}): number => {
    const priceAmount = (price?.convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    return Math.floor(priceAmount?.amount || 0);
};

export const showPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice = null } = item;

    if (!pricingGuidance) {
        return false;
    }

    const price = getPriceAmountByCurrency({ price: displayPrice?.[0], currency });

    if (!price) {
        return false;
    }

    return true;
};

export const resolveGuidanceType = ({
    item: itemRef,
}: {
    item: pricingGuidanceHelpers_item$key;
}): 'SOLD' | 'AVAILABLE' | null => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidanceType } = item?.pricingGuidance || {};

    if (['RECENT_SOLD', 'MARKETPLACE_RETAIL'].includes(pricingGuidanceType || '')) {
        return 'SOLD';
    }

    if (['AVAILABLE_PRICING', 'MARKETPLACE_AVAILABLE_RETAIL'].includes(pricingGuidanceType || '')) {
        return 'AVAILABLE';
    }

    return null;
};

export const showSoldPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const guidanceType = resolveGuidanceType({ item: itemRef });

    if (guidanceType === 'SOLD') {
        return showPricingGuidance({ item: itemRef, currency });
    }

    return false;
};

export const showSiderailSoldPricingGuidance = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}: {
    viewer: pricingGuidanceHelpers_viewer$key;
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    const viewer = readInlineData(viewerFragment, viewerRef);

    return (
        !!viewer.pdpSiderailSoldPricingGuidanceLinkFF &&
        showSoldPricingGuidance({ item: itemRef, currency })
    );
};

export const getGuidancePriceAmount = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): number => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice } = item || {};
    const { price } = pricingGuidance || {};

    return getPriceAmountByCurrency({ price: price || displayPrice?.[0], currency });
};

export function isPriceRangeOrHigher({
    item: itemRef,
    targetPriceRange,
}: {
    item: pricingGuidanceHelpers_item$key;
    targetPriceRange: PricingGuidancePriceRange;
}): boolean {
    const item = readInlineData(itemFragment, itemRef);
    const { priceRange } = item?.pricingGuidance || {};

    return !!priceRange && priceRanges.indexOf(priceRange) >= priceRanges.indexOf(targetPriceRange);
}
